import React, { useState } from 'react';
import {
  User, Mail, Phone, Lock, Bell, Shield, Save, Check,
  Eye, EyeOff, Camera, ChevronRight, MessageSquare, 
  Smartphone, X
} from 'lucide-react';

// Mock user data
const userData = {
  name: 'Marcus Johnson',
  email: 'marcus.johnson@email.com',
  phone: '+1 (876) 555-0123',
  shippingCode: 'MJ-4521',
  memberSince: 'March 2024',
  avatar: null
};

export default function CustomerSettings() {
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // Form states
  const [profile, setProfile] = useState({
    name: userData.name,
    email: userData.email,
    phone: userData.phone
  });

  const [notifications, setNotifications] = useState({
    emailPackageReady: true,
    emailInvoice: true,
    emailPayment: true,
    whatsappPackageReady: true,
    whatsappTransit: true,
    smsReady: false,
    smsPayment: false
  });

  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }, 1000);
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  return (
    <div className="customer-settings-page" data-testid="customer-settings">
      <div className="settings-layout">
        {/* Settings Navigation */}
        <div className="settings-nav">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`settings-nav-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={18} />
              <span>{tab.label}</span>
              <ChevronRight size={16} className="arrow" />
            </button>
          ))}
        </div>

        {/* Settings Content */}
        <div className="settings-content">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="settings-section" data-testid="profile-section">
              <div className="section-header">
                <h2>Profile Information</h2>
                <p>Manage your personal information and contact details.</p>
              </div>

              {/* Avatar */}
              <div className="settings-card avatar-card">
                <div className="avatar-section">
                  <div className="avatar-large">
                    {userData.avatar ? (
                      <img src={userData.avatar} alt="Profile" />
                    ) : (
                      <span>{userData.name.split(' ').map(n => n[0]).join('')}</span>
                    )}
                  </div>
                  <div className="avatar-info">
                    <h3>{userData.name}</h3>
                    <p>Shipping Code: {userData.shippingCode}</p>
                    <p className="member-since">Member since {userData.memberSince}</p>
                  </div>
                </div>
                <button className="btn btn-secondary upload-avatar">
                  <Camera size={14} /> Change Photo
                </button>
              </div>

              {/* Personal Info */}
              <div className="settings-card">
                <h3>Personal Details</h3>
                <div className="form-group">
                  <label>Full Name</label>
                  <div className="input-with-icon">
                    <User size={18} />
                    <input
                      type="text"
                      value={profile.name}
                      onChange={(e) => setProfile({...profile, name: e.target.value})}
                      data-testid="name-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email Address</label>
                  <div className="input-with-icon">
                    <Mail size={18} />
                    <input
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({...profile, email: e.target.value})}
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Phone Number</label>
                  <div className="input-with-icon">
                    <Phone size={18} />
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) => setProfile({...profile, phone: e.target.value})}
                      data-testid="phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Code (Read-only) */}
              <div className="settings-card readonly-card">
                <h3>Shipping Information</h3>
                <div className="form-group">
                  <label>Your Shipping Code</label>
                  <div className="readonly-value">
                    <span className="code-display">{userData.shippingCode}</span>
                    <span className="hint">This code is used in your Florida shipping address</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="settings-section" data-testid="notifications-section">
              <div className="section-header">
                <h2>Notification Preferences</h2>
                <p>Choose how and when you want to be notified.</p>
              </div>

              {/* Email Notifications */}
              <div className="settings-card">
                <div className="card-title-row">
                  <Mail size={20} />
                  <h3>Email Notifications</h3>
                </div>
                
                <div className="toggle-setting">
                  <div className="toggle-info">
                    <h4>Package Ready for Pickup</h4>
                    <p>Get notified when your package is ready</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notifications.emailPackageReady}
                      onChange={(e) => setNotifications({...notifications, emailPackageReady: e.target.checked})}
                      data-testid="email-ready-toggle"
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="toggle-setting">
                  <div className="toggle-info">
                    <h4>Invoice Notifications</h4>
                    <p>Receive invoices via email</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notifications.emailInvoice}
                      onChange={(e) => setNotifications({...notifications, emailInvoice: e.target.checked})}
                      data-testid="email-invoice-toggle"
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="toggle-setting">
                  <div className="toggle-info">
                    <h4>Payment Confirmations</h4>
                    <p>Get confirmation when payments are processed</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notifications.emailPayment}
                      onChange={(e) => setNotifications({...notifications, emailPayment: e.target.checked})}
                      data-testid="email-payment-toggle"
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              {/* WhatsApp Notifications */}
              <div className="settings-card">
                <div className="card-title-row">
                  <MessageSquare size={20} />
                  <h3>WhatsApp Notifications</h3>
                </div>
                
                <div className="toggle-setting">
                  <div className="toggle-info">
                    <h4>Package Ready Alerts</h4>
                    <p>Instant WhatsApp message when package arrives</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notifications.whatsappPackageReady}
                      onChange={(e) => setNotifications({...notifications, whatsappPackageReady: e.target.checked})}
                      data-testid="whatsapp-ready-toggle"
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="toggle-setting">
                  <div className="toggle-info">
                    <h4>Transit Updates</h4>
                    <p>Get updates as your package moves</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notifications.whatsappTransit}
                      onChange={(e) => setNotifications({...notifications, whatsappTransit: e.target.checked})}
                      data-testid="whatsapp-transit-toggle"
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              {/* SMS Notifications */}
              <div className="settings-card">
                <div className="card-title-row">
                  <Smartphone size={20} />
                  <h3>SMS Notifications</h3>
                </div>
                
                <div className="toggle-setting">
                  <div className="toggle-info">
                    <h4>Package Ready SMS</h4>
                    <p>Text message when package is ready</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notifications.smsReady}
                      onChange={(e) => setNotifications({...notifications, smsReady: e.target.checked})}
                      data-testid="sms-ready-toggle"
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="toggle-setting">
                  <div className="toggle-info">
                    <h4>Payment Reminders</h4>
                    <p>SMS reminder for pending payments</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={notifications.smsPayment}
                      onChange={(e) => setNotifications({...notifications, smsPayment: e.target.checked})}
                      data-testid="sms-payment-toggle"
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="settings-section" data-testid="security-section">
              <div className="section-header">
                <h2>Security Settings</h2>
                <p>Manage your password and account security.</p>
              </div>

              <div className="settings-card">
                <h3>Password</h3>
                <p className="card-description">
                  Last changed 30 days ago. We recommend changing your password regularly.
                </p>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowPasswordModal(true)}
                  data-testid="change-password-btn"
                >
                  <Lock size={14} /> Change Password
                </button>
              </div>

              <div className="settings-card">
                <h3>Two-Factor Authentication</h3>
                <p className="card-description">
                  Add an extra layer of security to your account.
                </p>
                <div className="toggle-setting single">
                  <div className="toggle-info">
                    <h4>Enable 2FA</h4>
                    <p>Require a code from your phone when signing in</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" data-testid="2fa-toggle" />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div className="settings-card">
                <h3>Active Sessions</h3>
                <p className="card-description">
                  Manage devices where you're currently logged in.
                </p>
                <div className="session-item current">
                  <div className="session-info">
                    <span className="session-device">Chrome on Windows</span>
                    <span className="session-location">Kingston, Jamaica</span>
                  </div>
                  <span className="current-badge">Current</span>
                </div>
                <button className="btn btn-secondary" data-testid="logout-all-btn">
                  Log out all other sessions
                </button>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="settings-actions">
            <button 
              className={`btn btn-primary ${saved ? 'saved' : ''}`}
              onClick={handleSave}
              disabled={saving}
              data-testid="save-settings-btn"
            >
              {saving ? (
                <>Saving...</>
              ) : saved ? (
                <>
                  <Check size={16} /> Saved!
                </>
              ) : (
                <>
                  <Save size={16} /> Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div 
            className="modal-content password-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="password-modal"
          >
            <div className="modal-header">
              <h3>Change Password</h3>
              <button className="close-btn" onClick={() => setShowPasswordModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Current Password</label>
                <input type="password" placeholder="Enter current password" data-testid="current-pw" />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <input type="password" placeholder="Enter new password" data-testid="new-pw" />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input type="password" placeholder="Confirm new password" data-testid="confirm-pw" />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPasswordModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" data-testid="update-pw-btn">
                Update Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
